import Game from './game';
import BaseObject, { BaseObjectParams } from './baseObject';
import LevelScene from './scene/LevelScene';
import Camera from './camera';
import Projectile from './element/projectile';

import { Faction } from '../utils/faction';
import { Iterator } from '../utils/types';
import { CoordsType } from '../utils/geometry';
import { isBetween } from '../utils/math';


export interface MinimapParams extends BaseObjectParams {
  context: CanvasRenderingContext2D;

  width: number;
  height: number;
}

export default class Minimap extends BaseObject implements MinimapParams {
  context!: CanvasRenderingContext2D;
  width!: number;
  height!: number;

  borderWidth = 1;

  get scene() {
    return Game.instance.scene;
  }

  constructor(params: MinimapParams) {
    super();
    this.applyParams(params);

    if (!this.width)
      this.width = this.scene.width / 10;
    if (!this.height)
      this.height = this.scene.height / 10;
  }

  get camera(): Camera {
    return this?.scene?.camera;
  }

  get drawWidth(): number {
    return this.width / this.scene.zoomRatio;
  }

  get drawHeight(): number {
    return this.height / this.scene.zoomRatio;
  }

  get startX(): number {
    return this.camera.x - this.camera.width / 2 + this.borderWidth;
  }

  get endX(): number {
    return this.startX + this.drawWidth;
  }

  get startY(): number {
    return this.camera.y + this.camera.height / 2 - this.drawHeight - this.borderWidth;
  }

  get endY(): number {
    return this.startY + this.drawHeight;
  }

  areCoordsOnMinimap(coords: CoordsType): boolean {
    const { x, y } = coords;
    const { startX, endX, startY, endY } = this;
    return (
      isBetween(x, startX, endX)
      && isBetween(y, startY, endY)
    );
  }

  translateMinimapCoords(coords: CoordsType): CoordsType {
    const { x, y } = coords;
    const newCords = {
      x: (x - this.startX) * this.scene.width / this.drawWidth,
      y: (y - this.startY) * this.scene.height / this.drawHeight,
    } as CoordsType;
    return newCords;
  }

  draw(): CanvasRenderingContext2D {
    if (!this.camera) return this.context;
    const { context, camera, startX, startY, drawWidth, drawHeight, borderWidth } = this;

    const sizeRatio = drawWidth / this.scene.width;

    context.save();
    context.globalAlpha = 1;

    /* background */
    context.fillStyle = 'black';
    context.fillRect(
      startX,
      startY,
      drawWidth,
      drawHeight
    );

    /* Elements */
    const colorsByFaction: Iterator<string> = {
      [Faction.PLAYER]: 'darkgreen',
      [Faction.ENEMY]: 'red',
      [Faction.NEUTRAL]: 'yellow',
      [Faction.HAZARD]: 'orange',
    };
    Object.values(this.scene.elements)
      .filter(el => el.hasCollision) // only collideable elems
      .forEach((el) => {
        let elWidth = Math.max(el.width, 150) * sizeRatio;
        let elHeight = Math.max(el.height, 150) * sizeRatio;

        if (Game.instance.controlSchemes?.rts.selectedElementsIds.includes(el.id))
          context.fillStyle = 'lightgreen'; // selected elements appears colored with a distinct color on the minimap
        else if (el instanceof Projectile) {
          context.fillStyle = el.faction === Faction.PLAYER ? 'DarkGreen' : 'OrangeRed';
          elWidth /= 2;
          elHeight /= 2;
        }
        else
          context.fillStyle = colorsByFaction[(el.faction)] || 'blue';
        const elemRect = {
          x: startX + el.x * sizeRatio - elWidth / 2,
          y: startY + el.y * sizeRatio - elHeight / 2,
          width: elWidth,
          height: elHeight,
        };
        const { x, y, width, height } = elemRect;
        context.fillRect(x, y, width, height);
      });

    context.globalAlpha = 1;

    /* Warfog */
    const warFog = (this.scene as LevelScene)?.warFog;
    if (warFog?.temporaryCanvas) {
      context.drawImage(
        warFog.temporaryCanvas,
        startX,
        startY,
        drawWidth,
        drawHeight
      );
    }

    /* borders */
    context.strokeStyle = 'white';
    context.lineWidth = borderWidth / camera.zoomRatio;
    context.strokeRect(
      startX,
      startY,
      drawWidth,
      drawHeight
    );

    /* camera borders */
    const cameraRect = {
      x: startX + camera.x * sizeRatio - camera.width / 2 * sizeRatio,
      y: startY + camera.y * sizeRatio - camera.height / 2 * sizeRatio,
      width: camera.width * sizeRatio,
      height: camera.height * sizeRatio,
    };
    const { x: cameraX, y: cameraY, width: cameraWidth, height: cameraHeight } = cameraRect;
    context.strokeRect(cameraX, cameraY, cameraWidth, cameraHeight);

    context.restore();
    return context;
  }
}
